import React, { useCallback, memo, useState, useMemo } from "react";
import { CardViewX, Dropdown, Tag } from "@ann/web-components";
import { useToast } from "../../../../../../../components/toast";
import { archiveKnowledge } from "../../../../services/knowledge";
import EditKnowledgeModal from "../EditKnowledgeModal";
import { TagContainer } from "./KnowledgeCard.styled";
import ShareKnowledgeModalStandalone from "../../../../components/ShareKnowledgeModal/ShareKnowledgeModalStandalone";

const KnowledgeCard = memo(({
  knowledgeBase,
  onTrain,
  onManage,
  onArchive,
  onEditSuccess,
  departmentsList = [],
}) => {
  const toast = useToast();
  const [isArchiving, setIsArchiving] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // Defensive programming: handle both camelCase and snake_case formats
  const kb = useMemo(() => ({
    id: knowledgeBase.id || knowledgeBase.knowledgebase_uuid || knowledgeBase.knowledgebaseUuid,
    knowledgebaseUuid: knowledgeBase.knowledgebase_uuid || knowledgeBase.knowledgebaseUuid || knowledgeBase.id,
    name: knowledgeBase.name || "",
    description: knowledgeBase.description || "",
    owner: knowledgeBase.owner || "Unknown",
    collaborators: knowledgeBase.collaborators || 0,
    documents: knowledgeBase.documents || 0,
    category: knowledgeBase.category || "Unknown",
    status: knowledgeBase.status || "active",
  }), [knowledgeBase]);

  const getStatusTag = useCallback((status) => {
    if (status === "archived") {
      return {
        label: "Archived",
        type: "neutral",
      };
    }
    return {
      label: "Active",
      type: "success",
    };
  }, []);

  const handleArchive = useCallback(async () => {
    if (!kb.knowledgebaseUuid) {
      toast.error({
        message: "Knowledge base UUID is missing",
        canClose: true,
      });
      return;
    }

    setIsArchiving(true);
    try {
      await archiveKnowledge({
        knowledgebase_uuid: kb.knowledgebaseUuid,
      });

      const action = kb.status === "archived" ? "unarchived" : "archived";
      toast.success({
        message: `${kb.name} was ${action} successfully`,
        canClose: true,
      });

      // Notify parent to refresh the list
      if (onArchive) {
        onArchive(kb.knowledgebaseUuid);
      }
    } catch (error) {
      const errorMessage =
        error.message || "An error occurred while archiving knowledge base";
      toast.error({
        message: errorMessage,
        canClose: true,
      });
    } finally {
      setIsArchiving(false);
    }
  }, [kb.knowledgebaseUuid, kb.name, kb.status, toast, onArchive]);

  const handleEdit = useCallback(() => {
    setIsEditModalOpen(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
  }, []);

  const handleAddUsers = useCallback(() => {
    setIsShareModalOpen(true);
  }, []);

  const handleCloseShareModal = useCallback(() => {
    setIsShareModalOpen(false);
  }, []);

  const getMenuItems = useCallback(() => [
    {
      id: "edit",
      label: "Edit",
      onClick: handleEdit,
    },
    {
      id: "add-users",
      label: "Add Users",
      onClick: handleAddUsers,
    },
    {
      id: "archive",
      label: kb.status === "archived" ? "Unarchive" : "Archive",
      onClick: handleArchive,
      disabled: isArchiving,
    },
  ], [handleArchive, handleEdit, handleAddUsers, isArchiving, kb.status]);

  const handleTrain = useCallback(() => {
    if (onTrain) {
      onTrain(knowledgeBase);
    }
  }, [knowledgeBase, onTrain]);

  const handleManage = useCallback(() => {
    if (onManage) {
      onManage(kb.id, kb.name);
    }
  }, [kb.id, kb.name, onManage]);

  // Generate a consistent random color for the category tag based on knowledgeBase ID
  const tagColor = useMemo(() => {
    const colors = [
      "secondary",
      "success",
      "informative",
      "error",
      "alert",
      "neutral",
      "limeGreen",
      "yellow",
      "purple",
      "lightBlue"
    ];
    
    if (kb.id) {
      // Use ID as seed to generate a deterministic but seemingly random color
      const hash = kb.id.split('').reduce((acc, char) => {
        return char.charCodeAt(0) + ((acc << 5) - acc);
      }, 0);
      const index = Math.abs(hash) % colors.length;
      return colors[index];
    }
    
    // Fallback: random selection if no ID
    return colors[Math.floor(Math.random() * colors.length)];
  }, [kb.id]);

  const statusTag = getStatusTag(kb.status);
  const menuItems = getMenuItems();

  return (
    <CardViewX
      size="medium"
      showHover
      onCheckboxChange={() => {}}
      onMouseEnter={() => {}}
      onMouseLeave={() => {}}
    >
      <CardViewX.Header
        menu={
          <Dropdown
            items={menuItems}
            size="medium"
            type="tertiary"
          />
        }
        tag={{
          icon: kb.status === "archived" ? "archive" : "checkCircle",
          label: statusTag.label,
          type: statusTag.type,
        }}
        title={kb.name}
      />
     
      <CardViewX.Meta>
        <CardViewX.MetaItem
          icon="person"
          label={kb.owner}
          tooltipContent="Owner"
          color="primary-alt"
        />
        <CardViewX.MetaItem
          icon="group"
          label={`+${kb.collaborators}`}
          tooltipContent="Collaborators"
          color="yellow"
        />
        <CardViewX.MetaItem
          icon="description"
          label={`${kb.documents} documents`}
          tooltipContent="Total documents"
          color="lightBlue"
        />
      </CardViewX.Meta>
      <TagContainer>
        <Tag
          color={tagColor}
          size="medium"
          text={kb.category}
        />
      </TagContainer>
      <CardViewX.Actions>
        <CardViewX.SecondaryAction
          label="Train"
          onClick={handleTrain}
        />
        <CardViewX.PrimaryAction
          label="Manage"
          onClick={handleManage}
        />
      </CardViewX.Actions>
      <EditKnowledgeModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSuccess={onEditSuccess}
        initialData={knowledgeBase}
        departmentsList={departmentsList}
      />
      <ShareKnowledgeModalStandalone
        isOpen={isShareModalOpen}
        onClose={handleCloseShareModal}
        knowledgeBase={knowledgeBase}
      />
    </CardViewX>
  );
});

KnowledgeCard.displayName = "KnowledgeCard";

export default KnowledgeCard;

